import { CaretDown, Check } from '@/shared/icons/phosphor'
import { For, Show, createSignal, createUniqueId } from 'solid-js'
import { Loader } from '../../primitives/Loader'

export interface StepsDisclosureStep {
  label: string
  running?: boolean | undefined
}

export interface StepsDisclosureProps {
  label: string
  running: boolean
  steps: StepsDisclosureStep[]
  class?: string | undefined
}

const PILL =
  'inline-flex max-w-full items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[length:var(--text-sm)] text-[color:var(--color-text-muted)]'

export function StepsDisclosure(props: StepsDisclosureProps) {
  const listId = createUniqueId()
  const [open, setOpen] = createSignal(false)
  let list: HTMLOListElement | undefined
  const toggle = () => {
    const opening = !open()
    setOpen(opening)
    if (opening) {
      requestAnimationFrame(() => list?.scrollIntoView({ block: 'nearest' }))
    }
  }
  const status = () => [
    <Show
      when={props.running}
      fallback={
        <Check aria-hidden="true" class="h-4 w-4 shrink-0 text-[color:var(--color-success-600)]" />
      }
    >
      <span class="shrink-0" data-steps-running>
        <Loader size={16} shape="radial" />
      </span>
    </Show>,
    <span class="min-w-0 truncate">{props.label}</span>,
  ]

  return (
    <div class={`flex min-w-0 flex-col items-start gap-2 ${props.class ?? ''}`}>
      <div aria-live={props.running ? 'polite' : undefined} class="max-w-full">
        <Show
          when={props.steps.length > 0}
          fallback={
            <span data-steps-pill class={PILL}>
              {status()}
            </span>
          }
        >
          <button
            type="button"
            data-steps-pill
            aria-expanded={open()}
            aria-controls={listId}
            onClick={toggle}
            class={`${PILL} cursor-pointer transition-colors duration-[var(--duration-normal)] hover:border-[color-mix(in_srgb,var(--color-text)_24%,transparent)] hover:text-[color:var(--color-text)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary-500)]`}
          >
            {status()}
            <CaretDown
              aria-hidden="true"
              class={`h-3.5 w-3.5 shrink-0 transition-transform duration-[var(--duration-normal)] ${open() ? 'rotate-180' : ''}`}
            />
          </button>
        </Show>
      </div>
      <Show when={open() && props.steps.length > 0}>
        <ol ref={list} id={listId} class="flex flex-col gap-1.5 pl-3">
          <For each={props.steps}>
            {(step) => (
              <li class="flex items-center gap-2 text-[length:var(--text-sm)] text-[color:var(--color-text-muted)]">
                <Show
                  when={step.running}
                  fallback={
                    <span data-step-done class="shrink-0">
                      <Check
                        aria-hidden="true"
                        class="h-3.5 w-3.5 text-[color:var(--color-success-600)]"
                      />
                    </span>
                  }
                >
                  <span data-step-running class="shrink-0">
                    <Loader size={16} shape="radial" />
                  </span>
                </Show>
                {step.label}
              </li>
            )}
          </For>
        </ol>
      </Show>
    </div>
  )
}
